/**
 * Chromium applies `corner-shape: squircle` so large border-radius reads like iOS curves.
 * WebKit Safari mostly uses plain elliptical rounding, so the same px values look like pills.
 * We scale radii down on those engines so the silhouette matches Chrome.
 *
 * Older Chromium (before ~128) and some channels may not implement `corner-shape`; the property
 * is ignored and huge border-radius reads like broken Safari (pill / uneven corners). Treat those
 * like WebKit: scaled radii + no inline cornerShape (see supportsCornerShapeSquircle).
 */

export const CONTINUOUS_CORNER_PLAIN_RADIUS_SCALE = 0.58;

/**
 * True when the engine reliably supports CSS corner-shape squircle (Chrome 128+, recent Safari).
 * When false, use plain scaled border-radius and avoid setting cornerShape in inline styles.
 */
export function supportsCornerShapeSquircle() {
  if (typeof CSS === 'undefined' || typeof CSS.supports !== 'function') {
    return false;
  }
  try {
    return CSS.supports('corner-shape', 'squircle');
  } catch {
    return false;
  }
}

/** Spread into React style objects only when squircle will actually apply. */
export function getCornerShapeSquircleInlineStyle() {
  if (!supportsCornerShapeSquircle()) return {};
  return { cornerShape: 'squircle' };
}

export function shouldUseContinuousCornerRadiusCompensation() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/CriOS|EdgiOS/i.test(ua)) return false;
  if (navigator.vendor === 'Apple Computer, Inc.') return true;
  if (/FxiOS/i.test(ua)) return true;
  return false;
}

export function scaleContinuousCornerBorderRadius(px) {
  const n = Number(px);
  if (!Number.isFinite(n) || n <= 0) return px;
  return Math.max(6, Math.round(n * CONTINUOUS_CORNER_PLAIN_RADIUS_SCALE));
}

/** Use for inline border-radius when design assumes Chromium `corner-shape: squircle`. */
export function getPlainRadiusForContinuousCornerDesignPx(px) {
  const n = Number(px);
  if (!Number.isFinite(n)) return px;
  const rounded = Math.round(n);
  if (shouldUseContinuousCornerRadiusCompensation()) {
    return scaleContinuousCornerBorderRadius(rounded);
  }
  if (!supportsCornerShapeSquircle()) {
    return scaleContinuousCornerBorderRadius(rounded);
  }
  return rounded;
}

/**
 * Adds `safari-squircle-fallback` on `<html>` so **all** rules in
 * `styles/safari-squircle-fallback.css` apply (sidebar, modals, chat chrome, status page, etc.),
 * not only inline-styled components. Call once at boot (see index.js).
 *
 * Active when: Apple WebKit-style UA compensation applies, or `corner-shape: squircle` is not
 * supported (e.g. legacy Chromium — same broken look as old Safari without this file).
 */
export function applySafariSquircleFallbackClass() {
  if (!shouldUseContinuousCornerRadiusCompensation() && supportsCornerShapeSquircle()) {
    return;
  }
  document.documentElement.classList.add('safari-squircle-fallback');
}
